const VERIFIER_KEY = "pft.auth.pkce";
const STATE_KEY = "pft.auth.state";

const base64url = (bytes: Uint8Array) =>
  btoa(Array.from(bytes, (b) => String.fromCharCode(b)).join(""))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
const randomString = (length = 64) =>
  base64url(crypto.getRandomValues(new Uint8Array(length))).slice(0, length);
const sha256 = async (text: string) =>
  base64url(
    new Uint8Array(
      await crypto.subtle.digest("SHA-256", new TextEncoder().encode(text)),
    ),
  );

/** Error with an i18n key (`auth.<code>`); `message` is the English fallback. */
export class HostedUiError extends Error {
  constructor(
    readonly code: "ssoNotConfigured" | "signInIncomplete" | "signInRejected",
    message: string,
  ) {
    super(message);
    this.name = "HostedUiError";
  }
}

export interface Discovery {
  identityProvider?: string;
  hostedUiDomain?: string;
  clientId?: string;
}

const domainOf = (d: Discovery) => {
  const domain =
    d.hostedUiDomain || process.env.REACT_APP_COGNITO_HOSTED_UI_DOMAIN || "";
  return domain.startsWith("http") ? domain : `https://${domain}`;
};
const clientIdOf = (d: Discovery) =>
  d.clientId || process.env.REACT_APP_COGNITO_CLIENT_ID || "";
export const redirectUri = () => `${window.location.origin}/auth/callback`;

/** Sends the browser to the Cognito hosted UI with PKCE and the district's identity provider. */
export async function startHostedUiSignIn(
  discovery: Discovery,
  loginHint?: string,
): Promise<void> {
  if (
    !discovery.hostedUiDomain &&
    !process.env.REACT_APP_COGNITO_HOSTED_UI_DOMAIN
  ) {
    throw new HostedUiError(
      "ssoNotConfigured",
      "Single sign-on is not configured for this console build.",
    );
  }
  const verifier = randomString(96);
  const state = randomString(32);
  window.sessionStorage.setItem(VERIFIER_KEY, verifier);
  window.sessionStorage.setItem(STATE_KEY, state);
  const params = new URLSearchParams({
    client_id: clientIdOf(discovery),
    response_type: "code",
    scope: "openid email profile",
    redirect_uri: redirectUri(),
    code_challenge_method: "S256",
    code_challenge: await sha256(verifier),
    state,
  });
  if (discovery.identityProvider)
    params.set("identity_provider", discovery.identityProvider);
  if (loginHint) params.set("login_hint", loginHint);
  window.location.assign(
    `${domainOf(discovery)}/oauth2/authorize?${params.toString()}`,
  );
}

/** Exchanges the callback code for tokens; the verifier never leaves this browser. */
export async function completeHostedUiSignIn(
  search: string,
): Promise<{ accessToken: string; refreshToken: string; idToken?: string }> {
  const query = new URLSearchParams(search);
  const code = query.get("code");
  const state = query.get("state");
  const verifier = window.sessionStorage.getItem(VERIFIER_KEY);
  const expectedState = window.sessionStorage.getItem(STATE_KEY);
  window.sessionStorage.removeItem(VERIFIER_KEY);
  window.sessionStorage.removeItem(STATE_KEY);
  // The provider's own error text is not shown: it is untranslated and may use wording the
  // console avoids (FR-023).
  if (
    query.get("error") ||
    !code ||
    !verifier ||
    !state ||
    state !== expectedState
  )
    throw new HostedUiError(
      "signInIncomplete",
      "Sign-in didn't complete. Try again.",
    );
  const body = new URLSearchParams({
    grant_type: "authorization_code",
    client_id: clientIdOf({}),
    code,
    redirect_uri: redirectUri(),
    code_verifier: verifier,
  });
  const res = await fetch(`${domainOf({})}/oauth2/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body,
  });
  if (!res.ok)
    throw new HostedUiError(
      "signInRejected",
      "The identity provider rejected the sign-in. Try again.",
    );
  const tokens = await res.json();
  return {
    accessToken: tokens.access_token,
    refreshToken: tokens.refresh_token,
    idToken: tokens.id_token,
  };
}
